import { Archivable, Creatable, Modifiable, UserInfo } from 'model/business-base';
import { ApprovalStatus, DeviationType } from 'model/business-enums';
import { Country } from 'model/business-keywords';
import { CountryApproval, CountryApprover, CountryRequest, TravelRequest } from 'model/forms/travel-request';
import { EntityBase, EnumInfo } from 'model/platform-types';
import { DeviationDetail } from 'model/forms/deviation-request';
import { FileParameter, FileResponse, UploadedFile } from 'model/essential-types';

export abstract class TravelRequestDetailApi {
  abstract getTravelRequest(id: number): Promise<TravelRequestDetail>;
  abstract createTravelRequest(travelRequest: TravelRequest): Promise<TravelRequestDetail>;
  abstract updateTravelRequest(id: number, travelRequest: TravelRequest): Promise<TravelRequestDetail>;
  abstract archiveTravelRequest(id: number): Promise<TravelRequestDetail>;
  abstract restoreTravelRequest(id: number): Promise<TravelRequestDetail>;
  abstract submitApprovalResponse(id: number, response: TravelRequestResponse): Promise<TravelRequestDetail>;
  abstract uploadAttachment(id: number | null, file: FileParameter): Promise<UploadedFile>;
  abstract downloadAttachment(id: number | null, attachment: UploadedFile): Promise<FileResponse>;
}

export interface TravelRequestDetail {
  data: TravelRequestData;
  airlinesApplicableDeviations: AirlineApplicableDeviationRequests[];
  history: TravelRequestHistoryRecord[];
  permissions: TravelRequestPermissions;
}

export interface TravelRequestData extends TravelRequest, EntityBase, Creatable, Modifiable, Archivable {
  countryRequests: CountryRequestData[];
  status: EnumInfo<ApprovalStatus>;
}

export interface CountryRequestData extends CountryRequest {
  approval: CountryApprovalData | null;
}

export interface CountryApprovalData extends CountryApproval {
  approvers: CountryApproverData[];
  status: EnumInfo<ApprovalStatus>;
}

export interface CountryApproverData extends CountryApprover, Modifiable {
  comment: string | null;
  status: EnumInfo<ApprovalStatus>;
}

export interface AirlineApplicableDeviationRequests {
  airlineId: number | null;
  airlineCustomName: string | null;
  applicableDeviationRequest: ApplicableDeviationRequest[];
}

export interface ApplicableDeviationRequest {
  id: number;
  deviationType: EnumInfo<DeviationType>;
  detail: DeviationDetail;
  validFrom: string;
  validTo: string;
  additionalInfo: string | null;
}

export interface TravelRequestPermissions {
  canEdit: boolean;
  canArchive: boolean;
  canRestore: boolean;
  canApprove: boolean;
  approvableItems: CountryApproverReference[];
}

export interface CountryApproverReference {
  countryId: number;
  approverId: number;
}

export interface TravelRequestHistoryRecord extends Creatable {
  requestStatus: EnumInfo<ApprovalStatus>;
  approvalStatus: EnumInfo<ApprovalStatus> | null;
  country: Country | null;
  approver: UserInfo | null;
  comment: string | null;
}

export interface TravelRequestResponse {
  countryId: number;
  approverId: number;
  statusValue: ApprovalStatus;
  comment: string | null;
}
